/**
 * Кнопка блокировки запуска с подтверждением в два шага.
 *
 * Текст подтверждения — часть проекта, а не украшение: в нём прямо сказано,
 * что работающий двигатель не заглохнет. Человек должен видеть это в момент
 * нажатия, а не в инструкции, которую он не прочтёт.
 *
 * Все настоящие проверки — на сервере. Здесь только то, что нужно человеку,
 * чтобы не ошибиться: имя машины и понятное последствие.
 */

import { useState } from 'react'

import { ApiError, api } from './api'
import { dict, type Lang } from './i18n'

type Props = {
  deviceId: number
  deviceName: string
  blocked: boolean
  lang: Lang
  onDone: () => void
}

export function BlockButton({ deviceId, deviceName, blocked, lang, onDone }: Props) {
  const t = dict(lang)
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function confirm() {
    setBusy(true)
    setError('')
    try {
      if (blocked) await api.unblock(deviceId)
      else await api.block(deviceId)
      setAsking(false)
      onDone()
    } catch (caught) {
      // Отказ по правилам безопасности и несработавшее реле приходят с
      // готовым текстом — показываем его целиком, он объясняет, что делать.
      setError(
        caught instanceof ApiError && caught.message ? caught.message : t.networkError,
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        className={'act ' + (blocked ? 'undo' : 'danger')}
        onClick={() => {
          setError('')
          setAsking(true)
        }}
      >
        {blocked ? t.unblockAction : t.blockAction}
      </button>

      {asking && (
        <div className="confirm" role="dialog" aria-modal="true">
          <div className="cbox">
            <h4>{blocked ? t.confirmUnblockTitle : t.confirmBlockTitle}</h4>
            <p>
              <b>{deviceName}</b>{' '}
              {blocked ? t.confirmUnblockText : t.confirmBlockText}
              {!blocked && ' ' + t.engineWarning}
            </p>

            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}

            <button
              className={'act ' + (blocked ? 'undo' : 'danger')}
              onClick={confirm}
              disabled={busy}
            >
              {busy ? t.working : blocked ? t.unblockAction : t.confirmYes}
            </button>
            <button className="act" onClick={() => setAsking(false)} disabled={busy}>
              {t.confirmNo}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
